import React, { useState, useEffect } from 'react';
import { TagIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

const TagSelector = ({
  selectedTags = [],
  onChange,
  maxTags = 10,
  disabled = false,
  className = ''
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async (search = '') => {
    try {
      setLoading(true);
      const response = await api.get('/admin/tags', {
        params: { search, size: 100 }
      });
      if (response.data.success) {
        setAvailableTags(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setShowDropdown(true);
    if (value.length >= 2) {
      fetchTags(value);
    } else if (value.length === 0) {
      fetchTags();
    }
  };

  const handleSelectTag = (tag) => {
    if (selectedTags.length >= maxTags) {
      alert(`Maximum ${maxTags} tags allowed`);
      return;
    }
    if (!selectedTags.find(t => t.id === tag.id)) {
      onChange([...selectedTags, tag]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveTag = (tagId) => {
    onChange(selectedTags.filter(t => t.id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!searchTerm.trim()) return;
    
    try {
      setCreating(true);
      const response = await api.post('/admin/tags', {
        name: searchTerm.trim()
      });
      
      if (response.data.success) {
        const newTag = response.data.data;
        handleSelectTag(newTag);
        fetchTags(); // Refresh tag list
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
      alert(error.response?.data?.message || 'Failed to create tag');
    } finally {
      setCreating(false);
    }
  };

  const filteredTags = availableTags.filter(tag => 
    !selectedTags.find(st => st.id === tag.id) &&
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canCreateNew = searchTerm.trim().length >= 2 && 
    !availableTags.find(t => t.name.toLowerCase() === searchTerm.toLowerCase());

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium"
            >
              <TagIcon className="w-3 h-3" />
              {tag.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag.id)}
                  className="ml-1 hover:text-indigo-900"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Tag Input */}
      {!disabled && selectedTags.length < maxTags && (
        <div className="relative">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search or create tags..."
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
            <TagIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Dropdown */}
          {showDropdown && (searchTerm.length >= 2 || filteredTags.length > 0) && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-3 text-center text-sm text-gray-500">Loading tags...</div>
              ) : (
                <>
                  {/* Existing Tags */}
                  {filteredTags.length > 0 ? (
                    <div className="py-1">
                      {filteredTags.map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleSelectTag(tag)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                        >
                          <TagIcon className="w-4 h-4 text-gray-400" />
                          <span>{tag.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : searchTerm.length >= 2 ? (
                    <div className="p-3 text-center text-sm text-gray-500">
                      No tags found
                    </div>
                  ) : null}

                  {/* Create New Tag */}
                  {canCreateNew && (
                    <div className="border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleCreateTag}
                        disabled={creating}
                        className="w-full px-3 py-2 text-left hover:bg-indigo-50 flex items-center gap-2 text-sm text-indigo-600 font-medium"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>
                          {creating ? 'Creating...' : `Create "${searchTerm}"`}
                        </span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Helper Text */}
      <div className="flex justify-between text-xs text-gray-500">
        <span>Add relevant tags to improve discoverability</span>
        <span>{selectedTags.length}/{maxTags} tags</span>
      </div>
    </div>
  );
};

export default TagSelector;
